import type { StyleTemplate, StyleTemplateDraft } from "@/features/studio-ai/types/studio-ai.types"

import { api } from "@/lib/api-client"

const BASE = "/studio-ai/style-templates"

export function createStyleTemplate(
  data: Omit<StyleTemplate, "id" | "user_id" | "is_default" | "created_at" | "updated_at">
): Promise<StyleTemplate> {
  return api.post(BASE, data)
}

export function listStyleTemplates(params?: {
  visibility?: string
  media_type?: string
}): Promise<StyleTemplate[]> {
  const searchParams = new URLSearchParams()
  if (params?.visibility) searchParams.set("visibility", params.visibility)
  if (params?.media_type) searchParams.set("media_type", params.media_type)

  const queryString = searchParams.toString()
  const url = queryString ? `${BASE}?${queryString}` : BASE
  return api.get(url)
}

export function getStyleTemplate(id: string): Promise<StyleTemplate> {
  return api.get(`${BASE}/${id}`)
}

export function updateStyleTemplate(
  id: string,
  data: Partial<StyleTemplate>
): Promise<StyleTemplate> {
  return api.put(`${BASE}/${id}`, data)
}

export async function deleteStyleTemplate(id: string): Promise<void> {
  await api.delete(`${BASE}/${id}`)
}

export async function getStyleTemplateDraft(): Promise<StyleTemplateDraft | null> {
  try {
    const response = (await api.get(`${BASE}/drafts/current`)) as
      | StyleTemplateDraft
      | Record<string, unknown>
    if (typeof response !== "object" || !("id" in response)) {
      return null
    }
    return response as StyleTemplateDraft
  } catch (error) {
    // 404 = no draft exists, which is expected
    // Other errors should be logged but not block the UI
    // eslint-disable-next-line no-console
    console.warn("[StyleTemplateDraft] Failed to fetch draft:", error)
    return null
  }
}

export async function upsertStyleTemplateDraft(
  data: Partial<
    Pick<
      StyleTemplateDraft,
      | "title"
      | "description"
      | "prompt_fragment"
      | "visibility"
      | "brand_colors"
      | "chat_messages"
      | "files_meta"
      | "pending_ai_update"
      | "version"
    >
  >
): Promise<StyleTemplateDraft> {
  return (await api.put(`${BASE}/drafts/current`, data)) as StyleTemplateDraft
}

export async function deleteStyleTemplateDraft(): Promise<void> {
  await api.delete(`${BASE}/drafts/current`)
}
